import { DivIcon } from 'leaflet';
import { LatLngExpression } from 'leaflet';
import { stressClassFor } from './formatters';
import { TelemetryPoint, bearingDeg, angleDiffDeg } from './telemetryUtils';

export type FlightSegmentType = 'straight' | 'climb' | 'sink' | 'turn';

export interface ColoredFlightPolyline {
  id: string;
  type: FlightSegmentType;
  color: string;
  positions: LatLngExpression[];
}

export interface FlightMovementMarker {
  id: string;
  timestamp: string;
  position: [number, number];
  icon: DivIcon;
  action: string;
  altM: number | null;
  spdKmh: number | null;
}

export type FlightHeuristicThresholds = {
  turnMinSpeedKmh: number;
  turnRateDegPerSec: number;
  climbMps: number;
  sinkMps: number;
  engineAltMaxM: number;
  engineSpeedMinKmh: number;
  engineSustainSec: number;
  engineWindowSec: number;
  circuitAltMaxM: number;
  circuitCloseMeters: number;
  circuitBoxMaxMeters: number;
  circuitMinPathMeters: number;
  circuitMinLowPoints: number;
};

export const flightThresholdDefaults: FlightHeuristicThresholds = {
  turnMinSpeedKmh: 80,
  turnRateDegPerSec: 3,
  climbMps: 1.5,
  sinkMps: -1.5,
  engineAltMaxM: 200,
  engineSpeedMinKmh: 30,
  engineSustainSec: 15,
  engineWindowSec: 180,
  circuitAltMaxM: 200,
  circuitCloseMeters: 180,
  circuitBoxMaxMeters: 1200,
  circuitMinPathMeters: 1500,
  circuitMinLowPoints: 30,
};

const parseNumberParam = (params: URLSearchParams, key: string): number | undefined => {
  const raw = params.get(key);
  if (raw === null || raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

export const getFlightThresholds = (): FlightHeuristicThresholds => {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      ...flightThresholdDefaults,
      turnMinSpeedKmh: parseNumberParam(params, 'turnSpeed') ?? flightThresholdDefaults.turnMinSpeedKmh,
      turnRateDegPerSec: parseNumberParam(params, 'turnRate') ?? flightThresholdDefaults.turnRateDegPerSec,
      climbMps: parseNumberParam(params, 'climb') ?? flightThresholdDefaults.climbMps,
      sinkMps: parseNumberParam(params, 'sink') ?? flightThresholdDefaults.sinkMps,
      engineAltMaxM: parseNumberParam(params, 'engineAlt') ?? flightThresholdDefaults.engineAltMaxM,
      engineSpeedMinKmh: parseNumberParam(params, 'engineSpeed') ?? flightThresholdDefaults.engineSpeedMinKmh,
      engineSustainSec: parseNumberParam(params, 'engineSustain') ?? flightThresholdDefaults.engineSustainSec,
      engineWindowSec: parseNumberParam(params, 'engineWindow') ?? flightThresholdDefaults.engineWindowSec,
      circuitAltMaxM: parseNumberParam(params, 'circuitAlt') ?? flightThresholdDefaults.circuitAltMaxM,
      circuitCloseMeters: parseNumberParam(params, 'circuitClose') ?? flightThresholdDefaults.circuitCloseMeters,
      circuitBoxMaxMeters: parseNumberParam(params, 'circuitBox') ?? flightThresholdDefaults.circuitBoxMaxMeters,
      circuitMinPathMeters: parseNumberParam(params, 'circuitMinPath') ?? flightThresholdDefaults.circuitMinPathMeters,
      circuitMinLowPoints: parseNumberParam(params, 'circuitMinPts') ?? flightThresholdDefaults.circuitMinLowPoints,
    };
  } catch {
    return flightThresholdDefaults;
  }
};

export const classifyFlightSegment = (
  p0: TelemetryPoint | null,
  p1: TelemetryPoint,
  p2: TelemetryPoint,
  dtSeconds: number,
  thr: FlightHeuristicThresholds,
): { type: FlightSegmentType; turnDir?: 'Left' | 'Right'; vs?: number } => {
  const speedKmh = typeof p2.speed === 'number' ? p2.speed : 0;

  let vs: number | undefined;
  if (
    typeof p2.altitude === 'number' &&
    typeof p1.altitude === 'number' &&
    Number.isFinite(dtSeconds) &&
    dtSeconds > 0
  ) {
    vs = (p2.altitude - p1.altitude) / dtSeconds;
  }

  if (p0 && speedKmh >= thr.turnMinSpeedKmh) {
    const h1 = bearingDeg(p0, p1);
    const h2 = bearingDeg(p1, p2);
    const signedDelta = angleDiffDeg(h1, h2);
    const rate = Math.abs(signedDelta) / dtSeconds;
    if (Number.isFinite(rate) && rate >= thr.turnRateDegPerSec) {
      return { type: 'turn', turnDir: signedDelta >= 0 ? 'Right' : 'Left', vs };
    }
  }

  if (typeof vs === 'number' && Number.isFinite(vs)) {
    if (vs >= thr.climbMps) return { type: 'climb', vs };
    if (vs <= thr.sinkMps) return { type: 'sink', vs };
  }

  return { type: 'straight', vs };
};

export const flightSegmentColor = (type: FlightSegmentType): string => {
  if (type === 'straight') return '#2563eb';
  if (type === 'climb') return '#7c3aed';
  if (type === 'sink') return '#f59e0b';
  return '#16a34a';
};

export const makeStressBarsHtml = (stress: number): string => {
  const filledCount = stress >= 5 ? 5 : stress >= 4 ? 4 : stress >= 3 ? 3 : stress >= 2 ? 2 : 1;
  const bars = Array.from({ length: 5 })
    .map((_, idx) => {
      const isFilled = idx < filledCount;
      return `<span class="flight-trail-bar b${idx + 1} ${isFilled ? 'filled' : 'empty'}"></span>`;
    })
    .join('');
  return `<span class="flight-trail-bars" aria-hidden="true">${bars}</span>`;
};

export const makeFlightMovementDivIcon = (glyph: string, stress: number): DivIcon => {
  const klass = stressClassFor(stress);
  const html = `<div class="flight-movement-badge ${klass}"><span class="flight-movement-glyph">${glyph}</span>${makeStressBarsHtml(stress)}</div>`;
  return new DivIcon({
    html,
    className: 'flight-movement-divicon',
    iconSize: [1, 1],
    iconAnchor: [12, 12],
  });
};

